import { registerApplication, start, addErrorHandler } from 'single-spa'
import { constructApplications, constructRoutes, constructLayoutEngine } from 'single-spa-layout'
import contextHelper from './helpers/context'
import routingHelper from './helpers/routing'
import ConfigHelper from './helpers/config'
import importHelper from './helpers/mfeImports'
import { handleSingleSpaError } from './helpers/errorHelper'
import initSentry from './helpers/sentry'
import { useCustomStylesheet } from '@wilken8/portal-ui-common/composables/useCustomStylesheet'
import { useCustomIcons } from '@wilken8/portal-ui-common/composables/useCustomIcons'

const configHelper = new ConfigHelper()

const initLayout = (imports, brandConfig) => {
  const routes = constructRoutes(document.querySelector('#single-spa-layout'))

  const applications = constructApplications({
    routes,
    loadApp: async ({ name }) => {
      const mfe = imports[name]
      const app = await mfe.loadApp()

      if (app) { // TODO: verfügbar?
        const initFn = app.init
        if (initFn instanceof Promise) {
          return await initFn(mfe.opts)
        }
        return initFn(mfe.opts)
      }

      return mfe
    }
  })

  const layoutEngine = constructLayoutEngine({ routes, applications })

  const dynamicApplicationRoutes = Object.entries(imports)
    .filter(([, i]) => i.include !== false && contextHelper.hasContext(i.opts.contextName, brandConfig) && i.buildActivityFn && i.opts?.routerBase)
    .map(([name, i]) => [name, i.opts.routerBase.replace(/^\/$/, '').replace(/\/$/, '')])

  const findLongestMatch = loc => {
    const longestMath = dynamicApplicationRoutes
      .filter(([, route]) => loc.startsWith(route))
      .sort(([, routeA], [, routeB]) => routeB.length - routeA.length)[0]
    return longestMath
  }

  applications.forEach(app => {
    const mfeImport = imports[app.name]
    if (mfeImport?.buildActivityFn) {
      if (!app.activeWhen) {
        app.activeWhen = []
      }
      const hasContext = contextHelper.hasContext(mfeImport.opts.contextName, brandConfig)
      if (!hasContext) {
        app.activeWhen[0] = () => false
        return
      }
      app.activeWhen[0] = () => {
        if (mfeImport.include === false) {
          return false
        }
        const location = window.location.pathname.replace(/^\/$/, '').replace(/\/$/, '')
        if (mfeImport.exactRouteMatch) {
          return location === mfeImport.opts.routerBase.replace(/^\/$/, '').replace(/\/$/, '')
        } else {
          const longestMatch = findLongestMatch(location)
          if (!longestMatch) {
            return false
          }
          const [matchName] = longestMatch
          return matchName === app.name
        }
      }
    }
  })
  return { applications, layoutEngine }
}

const loadRequiredResources = (config) => {
  const stylePromise = new Promise(resolve => {
    useCustomStylesheet({
      href: '/__publicPath__/resources/css/style.css',
      onloadFn: () => resolve()
    })
  })

  const { loadPromise } = useCustomIcons({ config, loadIcons: true })
  return Promise.all([stylePromise, loadPromise]).then(() => window.onResourcesLoaded())
}

const attachInitialAppHandler = (brandConfig) => {
  const initialMountAppHandler = () => {
    loadRequiredResources(brandConfig)
    window.removeEventListener('single-spa:first-mount', initialMountAppHandler)
  }
  window.addEventListener('single-spa:first-mount', initialMountAppHandler)
}

export const init = async () => {
  addErrorHandler(err => handleSingleSpaError(err))
  let loadedApps = []
  initSentry({ getActiveApps: () => routingHelper.getActiveContextApplications(loadedApps), ignoredApps: [] })
  const brandConfig = await configHelper.fetchConfig()
  attachInitialAppHandler(brandConfig)
  const imports = await importHelper.generateImports(configHelper.tenantConfig)
  routingHelper.initRoute(imports, configHelper.tenantConfig)

  const { applications, layoutEngine } = initLayout(imports, configHelper.tenantConfig)
  loadedApps = applications
  applications.forEach(registerApplication)
  layoutEngine.activate()
  start({ urlRerouteOnly: true })
}
